import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../shared/context/CartContext';
import { formatCurrency, formatCurrencyArabic, descriptionWithArabicCurrency } from '../../shared/utils/currency';
import { useToast } from '../../shared/hooks/useToast';
import { Product, DiscountProgress, CartItem as CartItemType, ProductVariant } from '../../shared/types';
import api from '../../shared/services/api';
import ProductCard from './ProductCard';
import CartItemSkeleton from './skeletons/CartItemSkeleton';
import { SkeletonLine } from './skeletons/BaseSkeleton';
import { trackInitiateCheckout } from '../../shared/utils/metaPixel';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2999; /* Above product detail page, below cart drawer */
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
`;

const DrawerContainer = styled.div<{ $isOpen: boolean; $rtl?: boolean }>`
  position: fixed;
  top: 0;
  inset-inline-end: 0;
  width: 420px;
  max-width: 90vw;
  height: 100vh;
  background-color: ${props => props.theme.colors.white};
  z-index: 3000; /* Above product detail page (z-index: 2000) */
  transform: translateX(${props => {
    if (props.$isOpen) return '0';
    return props.$rtl ? '-100%' : '100%';
  }});
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.$rtl ? '2px 0 10px rgba(0, 0, 0, 0.1)' : '-2px 0 10px rgba(0, 0, 0, 0.1)'};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100vw;
    max-width: 100vw;
  }
`;

const ScaledContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    transform: scale(0.8);
    transform-origin: top center;
    width: 125%;
    margin: 0 -12.5%;
  }
`;

const DrawerHeader = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  }
`;

const DrawerTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin: 0;
  line-height: 1.2;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
    stroke: currentColor;
  }
  
  &:hover {
    opacity: 0.7;
  }
`;

const DiscountProgressBar = styled.div`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background.secondary};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const DiscountMessage = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.3;
`;

const AchievedDiscountText = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: #10B981;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: 2px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const NextDiscountText = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const CollectionOfferLink = styled(Link)`
  color: ${props => props.theme.colors.black};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-decoration: underline;
  &:hover {
    text-decoration: none;
    opacity: 0.85;
  }
`;

const CheckpointProgressContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: ${props => props.theme.spacing.xs};
  padding-bottom: ${props => props.theme.spacing.xs};
`;

const CheckpointTrack = styled.div`
  width: 100%;
  height: 6px;
  background-color: ${props => props.theme.colors.gray?.[200] || '#e5e7eb'};
  border-radius: 3px;
  position: relative;
  overflow: visible;
`;

const CheckpointFill = styled.div<{ $progress: number; $isComplete: boolean }>`
  height: 100%;
  background-color: ${props => props.$isComplete ? '#10B981' : props.theme.colors.black};
  width: ${props => Math.min(props.$progress, 100)}%;
  transition: width 0.5s ease-in-out, background-color 0.3s ease-in-out;
  border-radius: 3px;
  position: relative;
  z-index: 1;
`;

const CheckpointMarkersContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  pointer-events: none;
`;

const CheckpointMarker = styled.div<{ $position: number; $achieved: boolean; $isNext: boolean }>`
  position: absolute;
  left: ${props => props.$position}%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: ${props => props.$achieved ? '14px' : props.$isNext ? '12px' : '10px'};
  height: ${props => props.$achieved ? '14px' : props.$isNext ? '12px' : '10px'};
  border-radius: 50%;
  background-color: ${props => props.$achieved ? '#10B981' : props.$isNext ? props.theme.colors.black : '#e5e7eb'};
  border: ${props => props.$achieved ? '1.5px solid white' : props.$isNext ? '1.5px solid white' : '1.5px solid #d1d5db'};
  z-index: ${props => props.$achieved ? 3 : props.$isNext ? 2 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$achieved || props.$isNext ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'};
  transition: all 0.3s ease-in-out;
  
  svg {
    width: ${props => props.$achieved ? '8px' : '0px'};
    height: ${props => props.$achieved ? '8px' : '0px'};
    color: white;
    stroke-width: 3;
  }
`;

const CheckpointLabelsContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 2px;
  height: 18px;
`;

const CheckpointLabel = styled.div<{ $position: number; $achieved: boolean; $isNext: boolean }>`
  position: absolute;
  left: ${props => props.$position}%;
  transform: translateX(-50%);
  text-align: center;
  font-size: 9px;
  color: ${props => props.$achieved ? '#10B981' : props.$isNext ? props.theme.colors.text.primary : props.theme.colors.text.secondary};
  font-weight: ${props => props.$achieved || props.$isNext ? props.theme.typography.fontWeight.medium : props.theme.typography.fontWeight.normal};
  white-space: nowrap;
  max-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DiscountBadge = styled.div<{ $position: number }>`
  position: absolute;
  left: ${props => props.$position}%;
  transform: translateX(-50%);
  bottom: 100%;
  margin-bottom: 2px;
  background-color: #10B981;
  color: white;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  white-space: nowrap;
  z-index: 4;
`;

const CartContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing['3xl']};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md};
    padding-bottom: 200px; /* Space for fixed footer above bottom nav */
  }
`;

const YouMayAlsoLikeSection = styled.div`
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border.light};
  width: 100%;
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg || '18px'};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin-bottom: ${props => props.theme.spacing.lg};
  margin-top: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${props => props.theme.colors.text.primary};
  text-align: left;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.typography.fontSize.base || '16px'};
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const RelatedProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.sm || '12px'};
  width: 100%;
  
  /* Ensure product cards fit properly in drawer */
  & > * {
    min-width: 0; /* Allow cards to shrink */
    width: 100%;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: ${props => props.theme.spacing.xs || '8px'};
  }
`;

const EmptyCartContainer = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing['3xl']} ${props => props.theme.spacing.lg};
`;

const EmptyCartText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CartItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const CartItem = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  margin-bottom: ${props => props.theme.spacing.sm};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: ${props => props.theme.borderRadius.sm};
  flex-shrink: 0;
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  min-width: 0;
  justify-content: space-between;
`;

const ItemTitle = styled(Link)`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ItemVariant = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const VariantSelectorContainer = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

// Helper function to map color names to hex values
const getColorHex = (colorName: string | undefined): string | undefined => {
  if (!colorName) return undefined;

  // If already a hex or rgb value, return as is
  if (colorName.startsWith('#') || colorName.startsWith('rgb')) {
    return colorName;
  }

  // Map color names to hex values
  const color = colorName.toLowerCase();
  const colorMap: Record<string, string> = {
    'black': '#000000',
    'white': '#FFFFFF',
    'grey': '#808080',
    'gray': '#808080',
    'light grey': '#D3D3D3',
    'light gray': '#D3D3D3',
    'blue': '#0000FF',
    'light blue': '#ADD8E6',
    'medium blue': '#0000CD',
    'dark blue': '#00008B',
    'navy': '#000080',
    'red': '#FF0000',
    'green': '#008000',
    'olive': '#808000',
    'beige': '#F5F5DC',
    'brown': '#A52A2A',
    'pink': '#FFC0CB',
    'soft pink': '#FFB6C1',
    'vintage': '#8B7355',
    'raw': '#D2B48C',
    'raw denim': '#1E3A5F',
    'acid wash': '#E6E6FA',
    'indigo': '#4B0082',
    'denim': '#1560BD',
    'khaki': '#C3B091',
    'tan': '#D2B48C',
    'cream': '#FFFDD0',
  };

  return colorMap[color] || undefined;
};

const VariantsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const VariantGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  flex-wrap: wrap;
`;

const VariantLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  text-transform: capitalize;
  white-space: nowrap;
`;

const VariantOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
`;

const VariantButton = styled.button<{ $isSelected: boolean; $isColor?: boolean; $colorValue?: string; $hasImage?: boolean }>`
  padding: ${props => props.$isColor && !props.$hasImage ? '0' : props.$isColor ? '3px' : `${props.theme.spacing.xs} ${props.theme.spacing.sm}`};
  border: ${props => props.$isColor ? '2px' : '2px'} solid ${props => props.$isSelected ? props.theme.colors.black : props.theme.colors.border.light};
  background-color: ${props => {
    if (props.$isColor && props.$colorValue && !props.$hasImage) {
      return props.$colorValue;
    }
    return props.theme.colors.white;
  }};
  color: ${props => props.$isSelected ? props.theme.colors.text.primary : props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.xs};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  width: ${props => props.$isColor ? '40px' : 'auto'};
  height: ${props => props.$isColor ? '40px' : 'auto'};
  min-width: ${props => props.$isColor ? '40px' : 'auto'};
  min-height: ${props => props.$isColor ? '40px' : 'auto'};
  border-radius: ${props => props.$isColor ? '50%' : '4px'};
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 50%;
  }
  
  &:hover {
    border-color: ${props => props.theme.colors.black};
    transform: ${props => props.$isColor ? 'scale(1.05)' : 'none'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ItemPrice = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: #A5A5A5; /* rgb(165, 165, 165) - exact match from mitcha.com */
  margin-right: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.fontSize.sm}; /* 14px - matching mitcha.com */
  font-weight: ${props => props.theme.typography.fontWeight.normal}; /* 400 - matching mitcha.com */
`;

const SalePrice = styled.span`
  color: #F81212; /* rgb(248, 18, 18) - exact match from mitcha.com */
  font-size: ${props => props.theme.typography.fontSize.sm}; /* 14px - matching mitcha.com */
  font-weight: ${props => props.theme.typography.fontWeight.normal}; /* 400 - matching mitcha.com */
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  margin-top: 4px;
  width: 100%;
  justify-content: space-between;
`;

const QuantityControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${props => props.theme.colors.border.light};
  background: ${props => props.theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all 0.2s ease-in-out;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.black};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  height: 28px;
  border: 1px solid ${props => props.theme.colors.border.light};
  text-align: center;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text.secondary};
  
  &:hover {
    color: ${props => props.theme.colors.text.primary};
  }
  
  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
  }
`;

const DrawerFooter = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border.light};
  background-color: ${props => props.theme.colors.white};
  position: sticky;
  bottom: 0;
  z-index: ${props => props.theme.zIndex.modal + 1};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    position: fixed;
    left: 0;
    right: 0;
    /* Sit above bottom nav (~64px) + safe area so Checkout is always visible */
    bottom: calc(64px + env(safe-area-inset-bottom, 0px));
    width: 100%;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: ${props => props.theme.zIndex.modal + 1};
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
    padding-bottom: calc(${props => props.theme.spacing.xs} + env(safe-area-inset-bottom, 0px));
    margin: 0;
  }
`;

const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  line-height: 1.3;
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.black};
  color: ${props => props.theme.colors.white};
  border: none;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    font-size: ${props => props.theme.typography.fontSize.sm};
  }
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContinueShoppingButton = styled(Link)`
  display: block;
  text-align: center;
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text.secondary};
  text-decoration: none;
  font-size: ${props => props.theme.typography.fontSize.xs || '11px'};
  padding: ${props => props.theme.spacing.xs} 0;
  position: relative;
  z-index: ${props => props.theme.zIndex.modal + 2};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.typography.fontSize.xs || '11px'};
    padding: ${props => props.theme.spacing.xs} 0;
    margin-top: 4px;
  }
  
  &:hover {
    text-decoration: underline;
  }
`;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5"></path>
  </svg>
);

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language.startsWith('ar');
  const fmtMoney = (value: number) => (isAr ? formatCurrencyArabic(value) : formatCurrency(value));
  const fmtDesc = (description: string) => (isAr ? descriptionWithArabicCurrency(description) : description);
  const formatBuyXGetYDesc = (d: DiscountProgress): string => {
    const buyCount = d.buyCount ?? 1;
    const discountCount = d.discountCount ?? 1;
    const scope = d.scopeName || d.collectionName || '';

    if (d.type === 'percentage' && d.value >= 100 && discountCount === 1 && buyCount === 1) {
      return scope
        ? t('cart.discountBuyOneFreeScoped', { scope })
        : t('cart.discountBuyOneFree');
    }

    if (d.type === 'percentage' && discountCount === 1 && buyCount === 1) {
      return scope
        ? t('cart.discountBuyOnePercentSecondScoped', { scope, value: d.value })
        : t('cart.discountBuyOnePercentSecond', { value: d.value });
    }

    return fmtDesc(d.description);
  };
  const { cart, updateCartItem, removeFromCart, addToCart, refreshCart } = useCart();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [bestSellers, setBestSellers] = useState<Product[]>([]);
  const [loadingBestSellers, setLoadingBestSellers] = useState(false);
  const [updatingQuantity, setUpdatingQuantity] = useState<string | null>(null);
  const [availableDiscounts, setAvailableDiscounts] = useState<DiscountProgress[]>([]);
  const [productDetails, setProductDetails] = useState<Record<string, Product>>({});
  const [loadingVariants, setLoadingVariants] = useState<Record<string, boolean>>({});

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fetch available discounts when cart opens or items/subtotal change (backend uses session to get cart)
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!isOpen) return;

      try {
        const subtotal = cart?.subtotal || 0;
        const response = await api.get<DiscountProgress[]>('/cart/discounts', {
          params: { subtotal }
        });
        const discounts = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
        setAvailableDiscounts(discounts);
      } catch (_error) {
        setAvailableDiscounts([]);
      }
    };

    fetchDiscounts();
  }, [isOpen, cart?.subtotal, cart?.items?.length]);

  // Fetch best sellers when cart opens
  useEffect(() => {
    const fetchBestSellers = async () => {
      if (!isOpen) return;

      setLoadingBestSellers(true);
      try {
        const response = await api.get<Product[]>('/products/bestsellers', {
          params: { count: 6 }
        });
        setBestSellers(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch best sellers:', error);
        setBestSellers([]);
      } finally {
        setLoadingBestSellers(false);
      }
    };

    if (isOpen) {
      fetchBestSellers();
    }
  }, [isOpen]);

  // Fetch related products when cart has items
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!cart || cart.items.length === 0) {
        setRelatedProducts([]);
        return;
      }

      setLoadingRelated(true);
      try {
        // Get a random product from cart to find related products
        const randomItem = cart.items[Math.floor(Math.random() * cart.items.length)];
        const productId = randomItem.productId;

        // Fetch related products from the same collection or category
        const response = await api.get('/products', {
          params: {
            limit: 14,
            exclude: productId
          }
        });

        setRelatedProducts(response.data.items?.slice(0, 14) || []);
      } catch (error) {
        console.error('Failed to fetch related products:', error);
        setRelatedProducts([]);
      } finally {
        setLoadingRelated(false);
      }
    };

    if (isOpen && cart && cart.items.length > 0) {
      fetchRelatedProducts();
    }
  }, [isOpen, cart]);

  const subtotal = cart?.subtotal || 0;

  // Split into subtotal-based, quantity-based (collection), and buy-x-get-y-off discounts
  const subtotalDiscounts = availableDiscounts.filter(d => d.requirementType === 'subtotal');
  const quantityDiscounts = availableDiscounts.filter(d => d.requirementType === 'quantity');
  const buyXGetYOffDiscounts = availableDiscounts.filter(d => d.requirementType === 'buy_x_get_y_off');

  // Calculate discount progress for subtotal-based only
  const calculateDiscountProgress = () => {
    if (subtotalDiscounts.length === 0) {
      return {
        achievedDiscounts: [] as DiscountProgress[],
        nextDiscount: null as DiscountProgress | null,
        progressToNext: 0,
        maxThreshold: 0
      };
    }

    const achievedDiscounts = subtotalDiscounts.filter(d => subtotal >= d.minimumAmount);
    const nextDiscount = subtotalDiscounts.find(d => subtotal < d.minimumAmount) || null;
    const maxThreshold = subtotalDiscounts[subtotalDiscounts.length - 1]?.minimumAmount || 0;

    let progressToNext = 0;
    if (nextDiscount) {
      const previousThreshold = achievedDiscounts.length > 0
        ? achievedDiscounts[achievedDiscounts.length - 1].minimumAmount
        : 0;
      const range = nextDiscount.minimumAmount - previousThreshold;
      const currentProgress = subtotal - previousThreshold;
      progressToNext = range > 0 ? Math.min((currentProgress / range) * 100, 100) : 0;
    } else if (achievedDiscounts.length === subtotalDiscounts.length) {
      progressToNext = 100;
    }

    return {
      achievedDiscounts,
      nextDiscount,
      progressToNext,
      maxThreshold
    };
  };

  const discountProgress = calculateDiscountProgress();
  const { achievedDiscounts, nextDiscount, progressToNext, maxThreshold } = discountProgress;

  const overallProgress = maxThreshold > 0
    ? Math.min((subtotal / maxThreshold) * 100, 100)
    : 0;

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    if (updatingQuantity === itemId) return; // Prevent double clicks

    setUpdatingQuantity(itemId);
    try {
      if (newQuantity < 1) {
        await removeFromCart(itemId);
        showToast(t('cart.toastRemoved'), 'success');
      } else {
        await updateCartItem(itemId, newQuantity, undefined);
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
      showToast(t('cart.toastQtyFail'), 'error');
    } finally {
      setUpdatingQuantity(null);
    }
  };

  const handleRemove = async (itemId: string) => {
    await removeFromCart(itemId);
    showToast(t('cart.toastRemoved'), 'success');
  };

  const handleCheckout = () => {
    // Track InitiateCheckout (Meta + TikTok via metaPixel)
    if (cart) {
      trackInitiateCheckout({
        items: cart.items.map(item => ({ productId: item.productId, quantity: item.quantity })),
        subtotal: cart.subtotal
      });
    }

    onClose();
    navigate('/checkout');
  };

  // Fetch product details with variants
  const fetchProductDetails = async (productId: string) => {
    if (productDetails[productId] || loadingVariants[productId]) return; // Already fetched or loading

    setLoadingVariants(prev => ({ ...prev, [productId]: true }));
    try {
      const response = await api.get<Product>(`/products/${productId}`);
      setProductDetails(prev => ({ ...prev, [productId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    } finally {
      setLoadingVariants(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Auto-fetch product details when cart items change
  useEffect(() => {
    if (!cart || !isOpen) return;

    cart.items.forEach(item => {
      if (!productDetails[item.productId] && !loadingVariants[item.productId]) {
        fetchProductDetails(item.productId);
      }
    });
  }, [cart?.items, isOpen]);

  // Handle variant change
  const handleVariantChange = async (item: CartItemType, newVariantId: string) => {
    if (item.variantId === newVariantId) return; // Same variant, no change needed

    setUpdatingQuantity(item.id); // Use this to show loading state

    try {
      // Update variant using PUT endpoint - no remove/add needed
      await updateCartItem(item.id, undefined, newVariantId);
      showToast(t('cart.toastVariantOk'), 'success');
    } catch (error: any) {
      console.error('Failed to update variant:', error);
      const errorMessage = error?.response?.data?.error || t('cart.toastVariantFail');
      showToast(errorMessage, 'error');
    } finally {
      setUpdatingQuantity(null);
    }
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <DrawerContainer $isOpen={isOpen} $rtl={isAr}>
        <ScaledContentWrapper>
          <DrawerHeader>
            <DrawerTitle>{t('cart.title')}</DrawerTitle>
            <CloseButton onClick={onClose} aria-label={t('cart.closeAria')}>
              <CloseIcon />
            </CloseButton>
          </DrawerHeader>

          {(subtotalDiscounts.length > 0 || quantityDiscounts.length > 0 || buyXGetYOffDiscounts.length > 0) && (
            <DiscountProgressBar dir={isAr ? 'rtl' : 'ltr'}>
              {/* Subtotal-based progress (e.g. spend X LE to get Y% off) */}
              {subtotalDiscounts.length > 0 && (
                <>
                  <DiscountMessage>
                    {achievedDiscounts.length > 0 && (
                      <AchievedDiscountText>
                        <CheckIcon />
                        {t('cart.discountSubtotalActivated', { desc: fmtDesc(achievedDiscounts[achievedDiscounts.length - 1].description) })}
                      </AchievedDiscountText>
                    )}
                    {nextDiscount ? (
                      <NextDiscountText>
                        {t('cart.discountSubtotalAdd', {
                          amount: fmtMoney(Math.max(0, nextDiscount.minimumAmount - subtotal)),
                          desc: fmtDesc(nextDiscount.description),
                        })}
                      </NextDiscountText>
                    ) : achievedDiscounts.length === subtotalDiscounts.length && achievedDiscounts.length > 0 && (
                      <NextDiscountText>
                        ✓ {t('cart.discountSubtotalAll')}
                      </NextDiscountText>
                    )}
                  </DiscountMessage>
                  <CheckpointProgressContainer>
                    <CheckpointTrack>
                      <CheckpointFill
                        $progress={overallProgress}
                        $isComplete={achievedDiscounts.length === subtotalDiscounts.length}
                      />
                      <CheckpointMarkersContainer>
                        {subtotalDiscounts.map((discount) => {
                          const isAchieved = subtotal >= discount.minimumAmount;
                          const isNext = nextDiscount?.id === discount.id;
                          const position = maxThreshold > 0
                            ? (discount.minimumAmount / maxThreshold) * 100
                            : 0;
                          return (
                            <React.Fragment key={discount.id}>
                              {isAchieved && (
                                <DiscountBadge $position={position}>
                                  {fmtDesc(discount.description)}
                                </DiscountBadge>
                              )}
                              <CheckpointMarker
                                $position={position}
                                $achieved={isAchieved}
                                $isNext={isNext}
                              >
                                {isAchieved && <CheckIcon />}
                              </CheckpointMarker>
                            </React.Fragment>
                          );
                        })}
                      </CheckpointMarkersContainer>
                    </CheckpointTrack>
                    <CheckpointLabelsContainer>
                      {subtotalDiscounts.map((discount) => {
                        const isAchieved = subtotal >= discount.minimumAmount;
                        const isNext = nextDiscount?.id === discount.id;
                        const position = maxThreshold > 0
                          ? (discount.minimumAmount / maxThreshold) * 100
                          : 0;
                        return (
                          <CheckpointLabel
                            key={discount.id}
                            $position={position}
                            $achieved={isAchieved}
                            $isNext={isNext}
                            title={fmtMoney(discount.minimumAmount)}
                          >
                            {fmtMoney(discount.minimumAmount)}
                          </CheckpointLabel>
                        );
                      })}
                    </CheckpointLabelsContainer>
                  </CheckpointProgressContainer>
                </>
              )}
              {/* Buy X get Y off (e.g. buy 2 get 50% off the 3rd) */}
              {buyXGetYOffDiscounts.length > 0 && buyXGetYOffDiscounts.map((d) => {
                const groupSize = (d.buyCount ?? 2) + (d.discountCount ?? 1);
                const current = d.currentQuantity ?? 0;
                const remainder = current % groupSize;
                const neededForNext = remainder === 0 ? 0 : groupSize - remainder;
                const fullGroups = Math.floor(current / groupSize);
                const isAchieved = fullGroups > 0;
                const progressPct = groupSize > 0 ? Math.min((current / groupSize) * 100, 100) : 0;
                return (
                  <CheckpointProgressContainer key={d.id}>
                    <DiscountMessage>
                      {isAchieved ? (
                        <AchievedDiscountText>
                          <CheckIcon />
                          {t('cart.discountBuyDone', { desc: formatBuyXGetYDesc(d) })}
                        </AchievedDiscountText>
                      ) : (
                        <NextDiscountText>
                          {neededForNext === 0
                            ? formatBuyXGetYDesc(d)
                            : t('cart.discountBuyAdd', { count: neededForNext, desc: formatBuyXGetYDesc(d) })}
                        </NextDiscountText>
                      )}
                    </DiscountMessage>
                    <CheckpointTrack>
                      <CheckpointFill $progress={progressPct} $isComplete={isAchieved} />
                    </CheckpointTrack>
                  </CheckpointProgressContainer>
                );
              })}
              {/* Quantity-based (collection) offers */}
              {quantityDiscounts.length > 0 && quantityDiscounts.map((d) => {
                const current = d.currentQuantity ?? 0;
                const required = d.minimumQuantity ?? 0;
                const needed = Math.max(0, required - current);
                const isAchieved = current >= required;
                const progressPct = required > 0 ? Math.min((current / required) * 100, 100) : 0;
                const collectionNamePlain = d.collectionName || t('cart.collection');
                return (
                  <CheckpointProgressContainer key={d.id}>
                    <DiscountMessage>
                      {isAchieved ? (
                        <AchievedDiscountText>
                          <CheckIcon />
                          {t('cart.discountQtyDone', { desc: fmtDesc(d.description), collection: collectionNamePlain })}
                        </AchievedDiscountText>
                      ) : (
                        <NextDiscountText>
                          {needed === required
                            ? t('cart.discountQtyBuy', {
                                required,
                                collection: collectionNamePlain,
                                desc: fmtDesc(d.description),
                              })
                            : t('cart.discountQtyAdd', {
                                count: needed,
                                collection: collectionNamePlain,
                                desc: fmtDesc(d.description),
                              })}
                        </NextDiscountText>
                      )}
                    </DiscountMessage>
                    {d.collectionId && !isAchieved && (
                      <NextDiscountText style={{ marginTop: 4 }}>
                        <CollectionOfferLink to={`/collections/${d.collectionId}`} onClick={onClose}>
                          {t('discountPopup.viewCollection', { name: collectionNamePlain })}
                        </CollectionOfferLink>
                      </NextDiscountText>
                    )}
                    <CheckpointTrack>
                      <CheckpointFill $progress={progressPct} $isComplete={isAchieved} />
                    </CheckpointTrack>
                  </CheckpointProgressContainer>
                );
              })}
            </DiscountProgressBar>
          )}

          <CartContent>
            {!cart || cart.items.length === 0 ? (
              <>
                <EmptyCartContainer>
                  <EmptyCartText>{t('cart.empty')}</EmptyCartText>
                  <ContinueShoppingButton to="/" onClick={onClose}>
                    {t('cart.continueShopping')}
                  </ContinueShoppingButton>
                </EmptyCartContainer>
                {bestSellers.length > 0 && (
                  <YouMayAlsoLikeSection>
                    <SectionTitle>{t('cart.youMayLike')}</SectionTitle>
                    <RelatedProductsGrid>
                      {bestSellers.map((product) => (
                        <div key={product.id} onClick={onClose}>
                          <ProductCard
                            product={product}
                            isLarge={false}
                            isNew={false}
                          />
                        </div>
                      ))}
                    </RelatedProductsGrid>
                  </YouMayAlsoLikeSection>
                )}
              </>
            ) : (
              <>
                <CartItemsList>
                  {cart.items.map((item) => {
                    const hasDiscount = item.compareAtPrice && item.compareAtPrice > item.unitPrice;
                    const product = productDetails[item.productId];
                    const isLoadingVariants = loadingVariants[item.productId];

                    // Get variant options if product is loaded
                    const currentVariant = product?.variants?.find(v => v.id === item.variantId);

                    return (
                      <CartItem key={item.id}>
                        {item.productImageUrl && (
                          <ItemImage
                            src={item.productImageUrl}
                            alt={item.productTitle}
                          />
                        )}

                        <ItemDetails>
                          <div>
                            <ItemTitle
                              to={`/products/${item.productHandle || item.productId}`}
                              onClick={onClose}
                            >
                              {item.productTitle}
                            </ItemTitle>

                            {currentVariant?.size && (
                              <ItemVariant>
                                {t('product.sizeLine', { size: currentVariant.size })}
                              </ItemVariant>
                            )}

                            {product && product.variants && product.variants.length > 0 && (
                              <VariantSelectorContainer>
                                {isLoadingVariants ? (
                                  <SkeletonLine $width="100px" $height="12px" />
                                ) : (
                                  <VariantsList>
                                    <VariantGroup>
                                <VariantLabel>{t('cart.size')}</VariantLabel>
                                      <VariantOptions>
                                        {product.variants.map((variant: ProductVariant) => {
                                          const isSelected = variant.id === item.variantId;
                                          const isAvailable = variant.stockQuantity > 0;

                                          return (
                                            <VariantButton
                                              key={variant.id}
                                              $isSelected={isSelected}
                                              onClick={() => isAvailable && !isSelected && handleVariantChange(item, variant.id)}
                                              disabled={isSelected || updatingQuantity === item.id || !isAvailable}
                                              $isColor={false}
                                            >
                                              {variant.size}
                                            </VariantButton>
                                          );
                                        })}
                                      </VariantOptions>
                                    </VariantGroup>
                                  </VariantsList>
                                )}





















                              </VariantSelectorContainer>
                            )}
                          </div>

                          <QuantityControls>
                            <QuantityControlsLeft>
                              <QuantityButton
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                aria-label={t('cart.decreaseQty')}
                                disabled={updatingQuantity === item.id}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                              </QuantityButton>
                              <QuantityInput
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                                disabled={updatingQuantity === item.id}
                              />
                              <QuantityButton
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                aria-label={t('cart.increaseQty')}
                                disabled={updatingQuantity === item.id}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="12" y1="5" x2="12" y2="19"></line>
                                  <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                              </QuantityButton>
                              <ItemPrice>
                                {hasDiscount && (
                                  <OriginalPrice>{formatCurrency(item.compareAtPrice!)}</OriginalPrice>
                                )}
                                <SalePrice>{formatCurrency(item.unitPrice)}</SalePrice>
                              </ItemPrice>
                            </QuantityControlsLeft>
                            <RemoveButton
                              onClick={() => handleRemove(item.id)}
                              aria-label={t('cart.removeItem')}
                            >
                              <CloseIcon />
                            </RemoveButton>
                          </QuantityControls>
                        </ItemDetails>
                      </CartItem>
                    );
                  })}
                </CartItemsList>

                {bestSellers.length > 0 && (
                  <YouMayAlsoLikeSection>
                    <SectionTitle>{t('cart.bestSellers')}</SectionTitle>
                    <RelatedProductsGrid>
                      {bestSellers.map((product, index) => (
                        <div key={product.id} onClick={onClose}>
                          <ProductCard
                            product={product}
                            isLarge={false}
                            isNew={false}
                          />
                        </div>
                      ))}
                    </RelatedProductsGrid>
                  </YouMayAlsoLikeSection>
                )}

                {relatedProducts.length > 0 && (
                  <YouMayAlsoLikeSection>
                    <SectionTitle>{t('cart.youMayAlsoLike')}</SectionTitle>
                    <RelatedProductsGrid>
                      {relatedProducts.map((product, index) => (
                        <div key={product.id} onClick={onClose}>
                          <ProductCard
                            product={product}
                            isLarge={false}
                            isNew={index < 2}
                          />
                        </div>
                      ))}
                    </RelatedProductsGrid>
                  </YouMayAlsoLikeSection>
                )}
              </>
            )}
          </CartContent>
        </ScaledContentWrapper>

        {cart && cart.items.length > 0 && (
          <DrawerFooter>
            <SubtotalRow>
              <span>{t('cart.subtotal')}</span>
              <span>{formatCurrency(subtotal)}</span>
            </SubtotalRow>
            {cart.discountTotal > 0 && (
              <SubtotalRow>
                <span>{t('cart.discount')}</span>
                <span style={{ color: '#10B981' }}>-{formatCurrency(cart.discountTotal)}</span>
              </SubtotalRow>
            )}
            <SubtotalRow>
              <span>{t('cart.total')}</span>
              <span style={{ fontWeight: 700 }}>{formatCurrency(cart.total)}</span>
            </SubtotalRow>
            <CheckoutButton onClick={handleCheckout}>
              {t('cart.checkout')}
            </CheckoutButton>
            <ContinueShoppingButton to="/" onClick={onClose}>
              {t('cart.continueShopping')}
            </ContinueShoppingButton>
          </DrawerFooter>
        )}
      </DrawerContainer>
    </>
  );
};

export default CartDrawer;

